import { useState, useCallback, useEffect, useRef } from "react";
import FormField from "./FormField.jsx";
import FileUpload from "./FileUpload.jsx";
import CityPicker from "./CityPicker.jsx";
import LoadingSpinner from "../common/LoadingSpinner.jsx";
import ErrorMessage from "../common/ErrorMessage.jsx";
import SuccessMessage from "../common/SuccessMessage.jsx";
import TermsModal from "../common/TermsModal.jsx";
import {
  getValidationErrors,
  hasValidationErrors,
} from "../../utils/validation.js";
import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  COUNTRIES,
} from "../../utils/constants.js";
import firestoreService from "../../services/firestoreService.js";
import storageService from "../../services/storageService.js";

const RegistrationForm = ({
  onSubmit,
  isLoading: externalLoading = false,
  className = "",
}) => {
  // Form state
  const [formData, setFormData] = useState({
    userInfo: {
      fullName: "",
      username: "",
      country: "Ethiopia",
      cityId: "",
      email: "",
      mobileNumber: "",
      passportNumber: "",
      nationalId: "",
      referrerContact: "",
    },
    representativeInfo: {
      fullName: "",
      nationalId: "",
      cityId: "",
      kebele: "",
    },
    paymentReceipt: null,
    termsAccepted: false,
  });

  // UI state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [referrerCheckLoading, setReferrerCheckLoading] = useState(false);
  const [referrerValidation, setReferrerValidation] = useState(null);
  const [usernameStatus, setUsernameStatus] = useState(null); // null | "checking" | "available" | "taken"
  const usernameDebounceRef = useRef(null);

  const isLoading = externalLoading || isSubmitting;

  // Handle form field changes
  const handleFieldChange = useCallback(
    (e) => {
      const { name, value, type, checked } = e.target;
      const fieldValue = type === "checkbox" ? checked : value;

      setFormData((prev) => {
        const newFormData = { ...prev };

        // Handle nested field updates
        if (name.startsWith("representative")) {
          // Map field names correctly
          const fieldMapping = {
            representativeFullName: "fullName",
            representativeNationalId: "nationalId",
            representativeKebele: "kebele",
          };

          const actualFieldName = fieldMapping[name];
          if (actualFieldName) {
            newFormData.representativeInfo = {
              ...prev.representativeInfo,
              [actualFieldName]: fieldValue,
            };
          }
        } else if (name === "termsAccepted") {
          newFormData.termsAccepted = fieldValue;
        } else {
          // Regular user info fields
          newFormData.userInfo = {
            ...prev.userInfo,
            [name]: fieldValue,
          };
        }

        return newFormData;
      });

      // Clear field-specific error when user starts typing
      if (errors[name]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }

      // Clear general submit errors and success message when user starts new input
      if (submitError) {
        setSubmitError(null);
      }
      if (submitSuccess) {
        setSubmitSuccess(false);
      }
    },
    [errors, submitError, submitSuccess],
  );

  // Handle file upload
  const handleFileChange = useCallback(
    (e) => {
      const file = e.target.value; // FileUpload component passes the file as value

      setFormData((prev) => ({
        ...prev,
        paymentReceipt: file,
      }));

      // Clear file-specific error and success message
      if (errors.paymentReceipt) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.paymentReceipt;
          return newErrors;
        });
      }
      if (submitSuccess) {
        setSubmitSuccess(false);
      }
    },
    [errors.paymentReceipt, submitSuccess],
  );

  // Debounced username uniqueness check
  const handleUsernameChange = useCallback(
    (e) => {
      const { value } = e.target;
      // Update formData via normal handler
      setFormData((prev) => ({
        ...prev,
        userInfo: { ...prev.userInfo, username: value },
      }));
      if (errors.username) {
        setErrors((prev) => {
          const n = { ...prev };
          delete n.username;
          return n;
        });
      }

      // Validate format
      const trimmed = value.trim();
      if (!trimmed) {
        setUsernameStatus(null);
        return;
      }
      if (!/^[a-zA-Z0-9_]{3,20}$/.test(trimmed)) {
        setUsernameStatus("invalid");
        return;
      }

      setUsernameStatus("checking");
      if (usernameDebounceRef.current)
        clearTimeout(usernameDebounceRef.current);
      usernameDebounceRef.current = setTimeout(async () => {
        try {
          const taken = await firestoreService.checkUsernameExists(trimmed);
          setUsernameStatus(taken ? "taken" : "available");
        } catch {
          setUsernameStatus(null);
        }
      }, 600);
    },
    [errors.username],
  );

  // Handle user city selection (stores city ID)
  const handleUserCityChange = useCallback(
    (cityId) => {
      setFormData((prev) => ({
        ...prev,
        userInfo: { ...prev.userInfo, cityId },
      }));
      if (errors.cityId) {
        setErrors((prev) => {
          const e = { ...prev };
          delete e.cityId;
          return e;
        });
      }
    },
    [errors.cityId],
  );

  // Handle representative city selection (stores city ID)
  const handleRepCityChange = useCallback((cityId) => {
    setFormData((prev) => ({
      ...prev,
      representativeInfo: { ...prev.representativeInfo, cityId },
    }));
  }, []);

  // Email uniqueness check function (called only on form submission)
  const checkEmailUniqueness = async (email) => {
    try {
      const emailExists = await firestoreService.checkEmailExists(email);
      return emailExists;
    } catch (error) {
      console.error("Error checking email uniqueness:", error);
      throw new Error("Failed to check email availability. Please try again.");
    }
  };

  // Phone number uniqueness check function (called only on form submission)
  const checkPhoneUniqueness = async (phoneNumber) => {
    try {
      const phoneExists = await firestoreService.checkPhoneExists(phoneNumber);
      return phoneExists;
    } catch (error) {
      console.error("Error checking phone uniqueness:", error);
      throw new Error(
        "Failed to check phone number availability. Please try again.",
      );
    }
  };

  // Manual referrer validation function
  const handleCheckReferrer = async () => {
    const referrerContact = formData.userInfo.referrerContact;

    if (!referrerContact || referrerContact.trim().length < 3) {
      setReferrerValidation({
        isValid: false,
        message: "Please enter a valid email or phone number",
        referrerInfo: null,
      });
      return;
    }

    setReferrerCheckLoading(true);
    setReferrerValidation(null);

    try {
      const referrerExists = await firestoreService.checkReferrerExists(
        referrerContact.trim(),
      );
      if (referrerExists) {
        setReferrerValidation({
          isValid: true,
          message: `✓ Referrer found: ${referrerExists.fullName}`,
          referrerInfo: referrerExists,
        });
        // Clear any existing error
        if (errors.referrerContact) {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.referrerContact;
            return newErrors;
          });
        }
      } else {
        setReferrerValidation({
          isValid: false,
          message: "Referrer not found in the system",
          referrerInfo: null,
        });
        setErrors((prev) => ({
          ...prev,
          referrerContact:
            "Referrer not found in the system. Please check the email or phone number.",
        }));
      }
    } catch (error) {
      console.error("Error checking referrer:", error);
      setReferrerValidation({
        isValid: false,
        message: "Error validating referrer",
        referrerInfo: null,
      });
    } finally {
      setReferrerCheckLoading(false);
    }
  };

  // Validate form on change
  useEffect(() => {
    const validationErrors = getValidationErrors(formData);
    setErrors((prev) => {
      // Only update errors for fields that have been touched or have existing errors
      const newErrors = {};
      Object.keys(validationErrors).forEach((key) => {
        if (prev[key] || validationErrors[key]) {
          newErrors[key] = validationErrors[key];
        }
      });
      return newErrors;
    });
  }, [formData]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // Clear previous states
      setSubmitError(null);
      setSubmitSuccess(false);

      // Validate entire form
      const validationErrors = getValidationErrors(formData);

      if (hasValidationErrors(validationErrors)) {
        setErrors(validationErrors);
        // Focus on first error field
        const firstErrorField = Object.keys(validationErrors)[0];
        const fieldElement = document.querySelector(
          `[name="${firstErrorField}"]`,
        );
        if (fieldElement) {
          fieldElement.focus();
        }
        return;
      }

      setIsSubmitting(true);
      setUploadProgress(0);

      try {
        // Check username uniqueness
        if (formData.userInfo.username?.trim()) {
          const usernameTaken = await firestoreService.checkUsernameExists(
            formData.userInfo.username.trim(),
          );
          if (usernameTaken) {
            setErrors((prev) => ({
              ...prev,
              username:
                "This username is already taken. Please choose another.",
            }));
            setIsSubmitting(false);
            setUploadProgress(0);
            return;
          }
        }

        // Check email uniqueness before proceeding
        const emailExists = await checkEmailUniqueness(formData.userInfo.email);
        if (emailExists) {
          setErrors((prev) => ({
            ...prev,
            email: ERROR_MESSAGES.EMAIL_EXISTS,
          }));
          setIsSubmitting(false);
          setUploadProgress(0);
          // Focus on email field
          const emailField = document.querySelector('[name="email"]');
          if (emailField) {
            emailField.focus();
          }
          return;
        }

        // Check phone number uniqueness before proceeding
        const phoneExists = await checkPhoneUniqueness(
          formData.userInfo.mobileNumber,
        );
        if (phoneExists) {
          setErrors((prev) => ({
            ...prev,
            mobileNumber: ERROR_MESSAGES.PHONE_EXISTS,
          }));
          setIsSubmitting(false);
          setUploadProgress(0);
          // Focus on phone field
          const phoneField = document.querySelector('[name="mobileNumber"]');
          if (phoneField) {
            phoneField.focus();
          }
          return;
        }

        let receiptUrl = null;

        // Upload file if provided
        if (formData.paymentReceipt) {
          // Generate a temporary registration ID for file organization
          const tempRegistrationId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

          receiptUrl = await storageService.uploadFile(
            formData.paymentReceipt,
            tempRegistrationId,
            (progress) => setUploadProgress(progress),
          );
        }

        // Prepare registration data
        const registrationData = {
          userInfo: formData.userInfo,
          representativeInfo: formData.representativeInfo,
          receiptUrl,
          status: "pending",
        };

        // Create registration record
        const registrationId =
          await firestoreService.createRegistration(registrationData);

        // If we uploaded a file with temp ID, we should ideally move it to the correct location
        // For now, we'll keep the temp structure as it's still organized

        setSubmitSuccess(true);

        // Call external onSubmit if provided
        if (onSubmit) {
          await onSubmit({
            ...registrationData,
            id: registrationId,
          });
        }

        // Reset form immediately after successful submission
        setFormData({
          userInfo: {
            fullName: "",
            username: "",
            country: "Ethiopia",
            cityId: "",
            email: "",
            mobileNumber: "",
            passportNumber: "",
            nationalId: "",
            referrerContact: "",
          },
          representativeInfo: {
            fullName: "",
            nationalId: "",
            cityId: "",
            kebele: "",
          },
          paymentReceipt: null,
          termsAccepted: false,
        });
        setErrors({});
        setReferrerValidation(null);
        setUsernameStatus(null);

        // Keep success message visible (don't auto-hide)
        // User can submit another registration if needed
      } catch (error) {
        console.error("Registration submission error:", error);
        setSubmitError(error.message || ERROR_MESSAGES.NETWORK_ERROR);
      } finally {
        setIsSubmitting(false);
        setUploadProgress(0);
      }
    },
    [formData, onSubmit],
  );

  // Check if form is valid for submission
  const isFormValid =
    !hasValidationErrors(getValidationErrors(formData)) &&
    formData.termsAccepted;

  return (
    <div className={`registration-form ${className}`}>
      <form onSubmit={handleSubmit} noValidate>
        {/* User Information Section */}
        <fieldset className="form-section">
          <legend className="section-title">Personal Information</legend>

          <FormField
            label="Full Name"
            name="fullName"
            value={formData.userInfo.fullName}
            onChange={handleFieldChange}
            placeholder="Enter your full name"
            required
            disabled={isLoading}
            error={errors.fullName}
            formData={formData}
            autoComplete="name"
          />

          {/* Username field with live availability check */}
          <div className="form-field">
            <label className="form-label">
              Username <span className="required-indicator">*</span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                name="username"
                className="form-input"
                value={formData.userInfo.username}
                onChange={handleUsernameChange}
                placeholder="e.g. abebe_girma (letters, numbers, _)"
                disabled={isLoading}
                maxLength={20}
                autoComplete="username"
                style={{ paddingRight: "2.5rem" }}
              />
              {/* Status icon */}
              {usernameStatus && (
                <span
                  style={{
                    position: "absolute",
                    right: "0.75rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "1rem",
                    pointerEvents: "none",
                  }}
                >
                  {usernameStatus === "checking" && "⏳"}
                  {usernameStatus === "available" && "✅"}
                  {usernameStatus === "taken" && "❌"}
                  {usernameStatus === "invalid" && "⚠️"}
                </span>
              )}
            </div>
            {/* Helper / status text */}
            {usernameStatus === "available" && (
              <p
                style={{
                  fontSize: "0.8125rem",
                  color: "var(--color-success)",
                  marginTop: "4px",
                }}
              >
                ✓ Username is available
              </p>
            )}
            {usernameStatus === "taken" && (
              <p
                style={{
                  fontSize: "0.8125rem",
                  color: "var(--color-danger)",
                  marginTop: "4px",
                }}
              >
                This username is already taken
              </p>
            )}
            {usernameStatus === "invalid" && (
              <p
                style={{
                  fontSize: "0.8125rem",
                  color: "var(--color-warning, #f59e0b)",
                  marginTop: "4px",
                }}
              >
                3–20 characters, letters, numbers and _ only
              </p>
            )}
            {usernameStatus === "checking" && (
              <p
                style={{
                  fontSize: "0.8125rem",
                  color: "var(--text-secondary)",
                  marginTop: "4px",
                }}
              >
                Checking availability...
              </p>
            )}
            {errors.username && (
              <p
                style={{
                  fontSize: "0.8125rem",
                  color: "var(--color-danger)",
                  marginTop: "4px",
                }}
              >
                {errors.username}
              </p>
            )}
            {!usernameStatus && !errors.username && (
              <p
                style={{
                  fontSize: "0.8125rem",
                  color: "var(--text-secondary)",
                  marginTop: "4px",
                }}
              >
                You can also use this username to log in
              </p>
            )}
          </div>

          <FormField
            label="Country"
            name="country"
            type="select"
            value={formData.userInfo.country}
            onChange={handleFieldChange}
            required
            disabled={isLoading}
            error={errors.country}
            formData={formData}
            autoComplete="country"
            options={COUNTRIES}
            placeholder="Select your country"
          />

          <CityPicker
            label="City"
            value={formData.userInfo.cityId}
            onChange={handleUserCityChange}
            required
            disabled={isLoading}
            error={errors.cityId}
          />

          <FormField
            label="Email Address"
            name="email"
            type="email"
            value={formData.userInfo.email}
            onChange={handleFieldChange}
            placeholder="Enter your email address"
            required
            disabled={isLoading}
            error={errors.email}
            formData={formData}
            autoComplete="email"
          />

          <FormField
            label="Mobile Number"
            name="mobileNumber"
            type="tel"
            value={formData.userInfo.mobileNumber}
            onChange={handleFieldChange}
            placeholder="+1234567890"
            required
            disabled={isLoading}
            error={errors.mobileNumber}
            formData={formData}
            autoComplete="tel"
          />

          <FormField
            label="Passport Number"
            name="passportNumber"
            value={formData.userInfo.passportNumber}
            onChange={handleFieldChange}
            placeholder="Enter passport number"
            disabled={isLoading}
            error={errors.passportNumber}
            formData={formData}
            maxLength={9}
          />

          <FormField
            label="ID Number"
            name="nationalId"
            value={formData.userInfo.nationalId}
            onChange={handleFieldChange}
            placeholder="Enter ID Number"
            required
            disabled={isLoading}
            error={errors.nationalId}
            formData={formData}
            maxLength={16}
          />

          <div className="referrer-field-container">
            <FormField
              label="Referrer (Optional)"
              name="referrerContact"
              value={formData.userInfo.referrerContact}
              onChange={handleFieldChange}
              placeholder="Enter referrer's email or phone number"
              disabled={isLoading}
              error={errors.referrerContact}
              formData={formData}
              helpText="If someone referred you to this system, enter their email or phone number"
            />
            <button
              type="button"
              className="check-referrer-btn"
              onClick={handleCheckReferrer}
              disabled={
                isLoading ||
                referrerCheckLoading ||
                !formData.userInfo.referrerContact?.trim()
              }
            >
              {referrerCheckLoading ? "Checking..." : "Check Referrer"}
            </button>
          </div>
          {referrerValidation && (
            <div
              className={`referrer-validation ${referrerValidation.isValid ? "valid" : "invalid"}`}
            >
              {referrerValidation.message}
            </div>
          )}
        </fieldset>

        {/* Representative Information Section */}
        <fieldset className="form-section">
          <legend className="section-title">Representative Information</legend>

          <FormField
            label="Representative Full Name"
            name="representativeFullName"
            value={formData.representativeInfo.fullName}
            onChange={handleFieldChange}
            placeholder="Enter representative's full name"
            disabled={isLoading}
            error={errors.representativeFullName}
            formData={formData}
          />

          <FormField
            label="Representative ID Number"
            name="representativeNationalId"
            value={formData.representativeInfo.nationalId}
            onChange={handleFieldChange}
            placeholder="Enter representative's ID Number"
            disabled={isLoading}
            error={errors.representativeNationalId}
            formData={formData}
            maxLength={16}
          />

          <CityPicker
            label="Representative City"
            value={formData.representativeInfo.cityId}
            onChange={handleRepCityChange}
            disabled={isLoading}
            error={errors.representativeCityId}
          />

          <FormField
            label="Kebele"
            name="representativeKebele"
            value={formData.representativeInfo.kebele}
            onChange={handleFieldChange}
            placeholder="Enter kebele (e.g., 03, 12A)"
            disabled={isLoading}
            error={errors.representativeKebele}
            formData={formData}
            maxLength={3}
          />
        </fieldset>

        {/* Payment Receipt Section */}
        <fieldset className="form-section">
          <legend className="section-title">Payment Receipt</legend>

          <FileUpload
            label="Payment Receipt"
            name="paymentReceipt"
            value={formData.paymentReceipt}
            onChange={handleFileChange}
            required
            disabled={isLoading}
            error={errors.paymentReceipt}
            showPreview={true}
          />

          {isSubmitting && uploadProgress > 0 && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span className="progress-text">
                {Math.round(uploadProgress)}% uploaded
              </span>
            </div>
          )}
        </fieldset>

        {/* Terms and Conditions */}
        <fieldset className="form-section">
          <div className="checkbox-field">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="termsAccepted"
                checked={formData.termsAccepted}
                onChange={handleFieldChange}
                disabled={isLoading}
                required
              />
              <span className="checkbox-text">
                I accept the{" "}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsTermsModalOpen(true);
                  }}
                  className="terms-link"
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  terms and conditions
                </button>
                <span className="required-indicator" aria-label="required">
                  *
                </span>
              </span>
            </label>
            <ErrorMessage
              message={errors.termsAccepted}
              className="field-error"
            />
          </div>
        </fieldset>

        {/* Submit Button */}
        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={!isFormValid || isLoading}
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="small" />
                <span>Submitting Registration...</span>
              </>
            ) : (
              "Submit Registration"
            )}
          </button>
        </div>

        {/* Error and Success Messages */}
        <ErrorMessage message={submitError} className="submit-error" />
        <SuccessMessage
          message={
            submitSuccess ? SUCCESS_MESSAGES.REGISTRATION_SUBMITTED : null
          }
          className="submit-success"
        />
      </form>

      {/* Terms and Conditions Modal */}
      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />
    </div>
  );
};

export default RegistrationForm;
